import { Box, Button, Flex, Input, Option, Select, Text } from '@modusign/design-system';
import i18nIsoCountries from 'i18n-iso-countries';
import { type CountryCode } from 'libphonenumber-js';
import { useMemo } from 'react';

import { Survey } from '@app/containers/Modal/WelcomeSurveyModal';
import { countries } from '@app/libs/countries';
import {
  getFormattedSouthKoreaPhoneNumber,
  getSouthKoreaPhoneNumberWithZero,
  removeNotNumberFromString,
} from '@app/libs/inputFormat';
import { validatePhoneNumber, validateRegularPhoneNumber } from '@app/libs/validation';

const countryMap = countries.map((value) => {
  return {
    value: i18nIsoCountries.getAlpha2Code(value, navigator.language.substring(0, 2)) as CountryCode,
    text: value,
  };
});

interface PhoneNumberStepProps {
  isWorkspaceSurveyLoading: boolean;
  survey: Survey;
  setSurvey: (update: (survey: Survey) => Survey) => void;
  submitSurvey: () => void;
}

function PhoneNumberStep({
  isWorkspaceSurveyLoading,
  survey,
  setSurvey,
  submitSurvey,
}: PhoneNumberStepProps) {
  const phoneNumberValidateMessage = useMemo(() => {
    if (!survey.phoneNumber) {
      return '전화번호를 입력해 주세요';
    }
    const { success: isValidPhoneNumber } = validatePhoneNumber(survey.phoneNumber);
    const { success: isValidRegularPhoneNumber } = validateRegularPhoneNumber(survey.phoneNumber);
    if (!isValidPhoneNumber && !isValidRegularPhoneNumber) {
      return '정확한 전화번호를 입력해 주세요';
    }
    return '';
  }, [survey.phoneNumber]);

  const isFormValid = !!survey.countryCode && !phoneNumberValidateMessage;

  return (
    <>
      <Box fontSize={2} fontWeight="medium" mt={2}>
        이용 중 불편사항을 해결해 드리기 위해 정보를 받고 있어요.
      </Box>
      <Box mt={8}>
        <Box>
          <Text fontWeight="medium">휴대폰 또는 회사 전화번호</Text>
        </Box>
        <Flex mt={3}>
          <Box maxWidth="120px">
            <Select
              height="44px"
              required
              value={survey.countryCode}
              onChange={({ currentTarget }) =>
                setSurvey((prev) => ({
                  ...prev,
                  countryCode: currentTarget.value,
                  phoneNumber: '',
                }))
              }
            >
              {countryMap.map(({ value, text }) => (
                <Option key={value} value={value}>
                  {text}
                </Option>
              ))}
            </Select>
          </Box>
          <Box flex={1}>
            <Input
              autoFocus
              height="44px"
              isInvalid={!!phoneNumberValidateMessage}
              maxLength={30}
              ml={2}
              placeholder="- 없이 숫자만 입력"
              required
              sx={{
                '&:invalid': {
                  border: '1px solid #d00000',
                },
              }}
              type="tel"
              value={survey.phoneNumber}
              onChange={({ currentTarget }) => {
                if (survey.countryCode === 'KR') {
                  const formattedSouthKoreaPhoneNumber = getFormattedSouthKoreaPhoneNumber(
                    getSouthKoreaPhoneNumberWithZero(currentTarget.value)
                  );

                  setSurvey((prev) => ({
                    ...prev,
                    phoneNumber: formattedSouthKoreaPhoneNumber,
                  }));

                  return;
                }

                setSurvey((prev) => ({
                  ...prev,
                  phoneNumber: removeNotNumberFromString(currentTarget.value),
                }));
              }}
            />
            <Box color="#d00000" mt={1} px={3}>
              {phoneNumberValidateMessage}
            </Box>
          </Box>
        </Flex>
        <Flex alignItems="center" justifyContent="end" mt={9}>
          <Button
            disabled={!isFormValid}
            isLoading={isWorkspaceSurveyLoading}
            ml={6}
            variant="primary"
            onClick={submitSurvey}
          >
            설정 완료
          </Button>
        </Flex>
      </Box>
    </>
  );
}

export default PhoneNumberStep;
